import apiClient from "./config";
import { queryCache } from "../../lib/queryCache";

// --- REVIEWER ACTIONS ---

export const getReviewerById = async (id, force = false) => {
  if (!id) return null;
  const cacheKey = `reviewer_${id}`;

  if (!force) {
    const cached = queryCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  try {
    const response = await apiClient.get(`/reviewer/${id}`);
    console.log(`[API] getReviewerById success. Meta:`, response.data?.meta?.topic);
    if (response.data) {
      queryCache.set(cacheKey, response.data, 10 * 60 * 1000); // 10 mins TTL
    }
    return response.data;
  } catch (error) {
    console.error(`[API] getReviewerById ERROR:`, error.message);
    throw new Error(error.response?.data?.error || "Could not load reviewer content.");
  }
};

export const generateReviewer = async (payload) => {
  console.log('[API] generateReviewer called');
  try {
    const response = await apiClient.post("/reviewer/generate", payload);
    console.log('[API] generateReviewer success');
    return response.data;
  } catch (error) {
    console.error('[API] generateReviewer ERROR:', error.message);
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Failed to generate reviewer.";
    if (error && typeof error === "object") {
      error.message = apiMessage;
      throw error;
    }
    throw new Error(apiMessage);
  }
};

export const saveReviewer = async (data) => {
  console.log('[API] saveReviewer called');
  try {
    const response = await apiClient.post("/reviewer/save", data);
    console.log('[API] saveReviewer success');
    // Invalidate library and folder caches so new notes appear
    queryCache.invalidate("library_recent_notes");
    queryCache.invalidatePrefix("folder_files_");
    return response.data;
  } catch (error) {
    console.error('[API] saveReviewer ERROR:', error.message);
    throw new Error(error.response?.data?.error || "Failed to save to blockchain.");
  }
};

export const getUserReviewers = async () => {
  try {
    // Backend now identifies user via HttpOnly cookie
    const response = await apiClient.get(`/reviewer/user/all`);
    console.log('[API] getUserReviewers success');
    return response.data;
  } catch (error) {
    console.error('[API] getUserReviewers ERROR:', error.message);
    throw new Error(error.response?.data?.error || "Could not load dashboard.");
  }
};

export const getUserArchivedReviewers = async () => {
  try {
    const response = await apiClient.get(`/reviewer/user/archived`);
    console.log('[API] getUserArchivedReviewers success');
    return response.data;
  } catch (error) {
    console.error('[API] getUserArchivedReviewers ERROR:', error.message);
    throw new Error(error.response?.data?.error || "Could not load archived notes.");
  }
};

export const updateReviewer = async (id, data) => {
  try {
    const response = await apiClient.patch(`/reviewer/${id}`, data);
    // Invalidate specific reviewer cache and lists
    queryCache.invalidate(`reviewer_${id}`);
    queryCache.invalidate("library_recent_notes");
    queryCache.invalidatePrefix("folder_files_");
    return response.data;
  } catch (error) {
    console.error('[API] updateReviewer ERROR:', error.message);
    throw new Error(error.response?.data?.error || "Failed to update note.");
  }
};

export const redeemSharedCode = async (code, reviewerId = null) => {
  try {
    const response = await apiClient.post("/reviewer/shared/redeem", { code, reviewerId });
    queryCache.invalidate("library_recent_notes");
    return response.data;
  } catch (error) {
    console.error("[API] redeemSharedCode ERROR:", error.message);
    const err = new Error(error.response?.data?.error || "Failed to redeem shared code.");
    if (error.response?.data?.reviewerId) {
      err.reviewerId = error.response.data.reviewerId;
    }
    throw err;
  }
};

export const disableReviewerShareCode = async (id) => {
  try {
    const response = await apiClient.post(`/reviewer/${id}/share/disable`);
    queryCache.invalidate(`reviewer_${id}`);
    return response.data;
  } catch (error) {
    console.error("[API] disableReviewerShareCode ERROR:", error.message);
    throw new Error(error.response?.data?.error || "Failed to disable share code.");
  }
};

export const revokeReviewerSharedAccess = async (id) => {
  try {
    const response = await apiClient.post(`/reviewer/${id}/share/revoke`);
    queryCache.invalidate(`reviewer_${id}`);
    return response.data;
  } catch (error) {
    console.error("[API] revokeReviewerSharedAccess ERROR:", error.message);
    throw new Error(error.response?.data?.error || "Failed to revoke shared access.");
  }
};

export const deleteReviewer = async (id) => {
  console.log('[API] deleteReviewer called for ID:', id);
  try {
    const response = await apiClient.delete(`/reviewer/${id}`);
    console.log('[API] deleteReviewer success');
    // Invalidate specific reviewer and lists
    queryCache.invalidate(`reviewer_${id}`);
    queryCache.invalidate("library_recent_notes");
    queryCache.invalidatePrefix("folder_files_");
    return response.data;
  } catch (error) {
    console.error('[API] deleteReviewer ERROR:', error.message);
    throw new Error(error.response?.data?.error || "Failed to delete reviewer.");
  }
};

// Search users by email or name (used by ShareModal Direct Share tab)
export const searchUserByIdentifier = async (query) => {
  try {
    const response = await apiClient.get("/users/search", { params: { q: query } });
    return response.data; // { success, users: [{_id, firstName, lastName, email, avatar}] }
  } catch (error) {
    console.error("[API] searchUserByIdentifier ERROR:", error.message);
    throw new Error(error.response?.data?.message || "Search failed.");
  }
};

// Direct-share a reviewer to a specific user or multiple users by their userId(s)
export const directShareReviewer = async (reviewerId, targetUserIdOrIds) => {
  try {
    const payload = Array.isArray(targetUserIdOrIds)
      ? { targetUserIds: targetUserIdOrIds }
      : { targetUserId: targetUserIdOrIds };
    const response = await apiClient.post(`/reviewer/${reviewerId}/share/direct`, payload);
    return response.data; // { success, message, reviewer, sharedCount, skippedCount }
  } catch (error) {
    console.error("[API] directShareReviewer ERROR:", error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.message || "Failed to share reviewer.");
  }
};

export const saveReviewerReadingProgress = async (reviewerId, progress) => {
  const response = await apiClient.patch(`/reviewer/${reviewerId}/reading-progress`, { progress });
  queryCache.invalidate(`reviewer_${reviewerId}`);
  queryCache.invalidate("library_recent_notes");
  return response.data;
};
